import { useContext, useState } from 'react';
import { Alert as AlertText } from '@lmig/lmds-react-alert';
import { IconButton, IconTrash } from '@lmig/lmds-react-icons';
import { Button } from '@lmig/lmds-react-button';
import { Heading } from '@lmig/lmds-react-typography';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lmig/lmds-react-modal';
import 'Feature/Alerts/Alert.scss';
import { useLocalStorage } from 'Utilities/UseStorage';
import { useEffect } from 'react';
import { AlertsContext } from 'Feature/Alerts/AlertsContext';
import { Alert, IAlertsContext } from 'Feature/Alerts/Models/Alerts';
import RichText from 'Feature/PageContent/Shared/Tiles/RichText';
import { GridCol, GridRow } from '@lmig/lmds-react-grid';

type IAlertCard = Alert & { dismiss?: (id: string) => void };
type IAlerts = { indicateNewAlerts: (arg: boolean) => void; viewed: boolean };

export function Alerts({ indicateNewAlerts, viewed }: IAlerts) {
  const [alerts, setAlerts] = useState<IAlertCard[]>([]);
  const [viewedAlerts, setViewedAlerts] = useLocalStorage<string[]>('viewed_alerts', [], undefined);
  const alertsContext = useContext<IAlertsContext>(AlertsContext);

  useEffect(() => {
    alertsContext.getAlerts();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (alertsContext.alerts) {
      setAlerts(alertsContext.alerts);
      let hasNew: boolean = alertsContext.alerts.filter((alert) => !viewedAlerts?.includes(alert.Id)).length > 0;
      if (hasNew) indicateNewAlerts(hasNew);
    }
    // eslint-disable-next-line
  }, [alertsContext]);

  useEffect(() => {
    if (alerts && alerts.length)
      indicateNewAlerts(alerts.filter((alert) => !viewedAlerts?.includes(alert.Id)).length > 0);
    // passing indeicateNewAlerts as a dependency here will break any input elements in parent components once deployed to sitecore
    // eslint-disable-next-line
  }, [alerts, viewedAlerts]);

  useEffect(() => {
    if (viewed)
      setViewedAlerts({
        data: alerts.map((x) => {
          return x.Id;
        }),
      });
  }, [viewed, alerts, setViewedAlerts]);

  const dismiss = (id: string) => {
    /* mailbox alerts should delete banner alerts, so broadcast the change to context */
    if (id) {
      alertsContext.dismiss(id);
    }
  };

  return (
    <>
      {alerts && alerts.length ? (
        alerts.map((alert: Alert) => (
          <AlertCard
            Date={alert.Date}
            Type={alert.Type}
            Id={alert.Id}
            Headline={alert.Headline}
            Description={alert.Description}
            dismiss={dismiss}
            key={alert.Id}
          />
        ))
      ) : (
        <div>There are no alerts currently available</div>
      )}
    </>
  );
}

function AlertCard(props: IAlertCard) {
  const [prompt, setPrompt] = useState<boolean>(false);

  const getDate = () => {
    return props.Date === '0001-01-01T00:00:00' || !props.Date
      ? undefined
      : `${new Date(props.Date ?? '').getMonth() + 1}/${new Date(props.Date ?? '').getDate()}/${new Date(
          props.Date ?? '',
        ).getFullYear()}`;
  };

  const getFormattedDescription = () => {
    return `<div class="alert-description-wrapper">${props.Description}</div>`;
  };

  return (
    <>
      {prompt ? (
        <Modal isOpen={prompt} className="alert-confirmation-modal" onClose={() => setPrompt(false)}>
          <ModalBody>
            <ModalHeader>
              <Heading type="h4" style={{ margin: '0px' }}>
                Are you sure?
              </Heading>
            </ModalHeader>
            <br />
            <p>Please confirm your wish to delete this alert from your inbox.</p>
          </ModalBody>
          <ModalFooter>
            <GridRow gutterSize="fixed-16" gutters>
              <GridCol base={12} md={6}>
                <Button onClick={() => setPrompt(false)}>Cancel</Button>
              </GridCol>
              <GridCol base={12} md={6}>
                <Button
                  variant="primary"
                  onClick={() => {
                    if (props.dismiss) {
                      props.dismiss(props.Id);
                    }
                    setPrompt(false);
                  }}
                >
                  Delete
                </Button>
              </GridCol>
            </GridRow>
          </ModalFooter>
        </Modal>
      ) : null}
      <div className="alert-card">
        <div className="alert-body">
          <AlertText highlightType={props.Type === 'Alert' ? 'negative' : 'caution'}>{props.Headline}</AlertText>
          <RichText className="alert-description" content={getFormattedDescription()} />
        </div>
        <div className="alert-footer">
          <span>{getDate()}</span>
          <IconButton onClick={() => setPrompt(true)}>
            <IconTrash alt="Delete alert" size="16" />
          </IconButton>
        </div>
      </div>
    </>
  );
}
